/**
 * Interactive 3D Connected Commerce Particle Canvas (Three.js)
 * High performance, responsive, ambient node mesh
 */

import * as THREE from 'three';

export function initHeroCanvas() {
    const container = document.querySelector('#hero-3d-canvas');
    if (!container) return;

    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || 500;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 240;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    // Particle nodes
    const particleCount = window.innerWidth < 768 ? 45 : 85;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const velocities = [];

    for (let i = 0; i < particleCount; i++) {
        const x = (Math.random() - 0.5) * 380;
        const y = (Math.random() - 0.5) * 220;
        const z = (Math.random() - 0.5) * 120;

        positions[i * 3] = x;
        positions[i * 3 + 1] = y;
        positions[i * 3 + 2] = z;

        velocities.push({
            x: (Math.random() - 0.5) * 0.25,
            y: (Math.random() - 0.5) * 0.25,
            z: (Math.random() - 0.5) * 0.15
        });
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Helper to generate a smooth circular particle sprite
    function createCircleTexture() {
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.arc(32, 32, 28, 0, Math.PI * 2);
        ctx.fillStyle = '#0A2A5E';
        ctx.fill();
        return new THREE.CanvasTexture(canvas);
    }

    const circleTexture = createCircleTexture();

    // Particle material with primary dark blue (#0A2A5E)
    const pMaterial = new THREE.PointsMaterial({
        color: 0x0A2A5E,
        size: 5.5,
        map: circleTexture,
        transparent: true,
        opacity: 0.85,
        alphaTest: 0.05,
        blending: THREE.NormalBlending
    });

    const particles = new THREE.Points(geometry, pMaterial);
    scene.add(particles);

    // Dynamic Connecting Lines in primary dark blue
    const lineMaterial = new THREE.LineBasicMaterial({
        color: 0x0A2A5E,
        transparent: true,
        opacity: 0.16,
        blending: THREE.NormalBlending
    });

    const lineGeometry = new THREE.BufferGeometry();
    const maxLineCount = particleCount * 6;
    const linePositions = new Float32Array(maxLineCount * 6);
    lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));

    const lines = new THREE.LineSegments(lineGeometry, lineMaterial);
    scene.add(lines);

    // Mouse & Scroll interaction
    let mouseX = 0;
    let mouseY = 0;
    let targetX = 0;
    let targetY = 0;

    window.addEventListener('mousemove', e => {
        mouseX = (e.clientX - window.innerWidth / 2) * 0.08;
        mouseY = (e.clientY - window.innerHeight / 2) * 0.08;
    }, { passive: true });

    let scrollY = 0;
    window.addEventListener('scroll', () => {
        scrollY = window.scrollY || document.documentElement.scrollTop;
    }, { passive: true });

    let animationFrameId;

    function animate() {
        animationFrameId = requestAnimationFrame(animate);

        targetX += (mouseX - targetX) * 0.05;
        targetY += (mouseY - targetY) * 0.05;

        camera.position.x = targetX;
        camera.position.y = -targetY - (scrollY * 0.04);
        camera.lookAt(scene.position);

        const pos = geometry.attributes.position.array;
        let lineIdx = 0;

        for (let i = 0; i < particleCount; i++) {
            pos[i * 3] += velocities[i].x;
            pos[i * 3 + 1] += velocities[i].y;
            pos[i * 3 + 2] += velocities[i].z;

            // Bounce back inside bounds
            if (pos[i * 3] < -200 || pos[i * 3] > 200) velocities[i].x *= -1;
            if (pos[i * 3 + 1] < -120 || pos[i * 3 + 1] > 120) velocities[i].y *= -1;
            if (pos[i * 3 + 2] < -70 || pos[i * 3 + 2] > 70) velocities[i].z *= -1;

            // Connect nearby nodes
            for (let j = i + 1; j < particleCount; j++) {
                const dx = pos[i * 3] - pos[j * 3];
                const dy = pos[i * 3 + 1] - pos[j * 3 + 1];
                const dz = pos[i * 3 + 2] - pos[j * 3 + 2];
                const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

                if (dist < 55 && lineIdx < maxLineCount * 6 - 6) {
                    linePositions[lineIdx++] = pos[i * 3];
                    linePositions[lineIdx++] = pos[i * 3 + 1];
                    linePositions[lineIdx++] = pos[i * 3 + 2];

                    linePositions[lineIdx++] = pos[j * 3];
                    linePositions[lineIdx++] = pos[j * 3 + 1];
                    linePositions[lineIdx++] = pos[j * 3 + 2];
                }
            }
        }

        geometry.attributes.position.needsUpdate = true;
        lineGeometry.attributes.position.needsUpdate = true;
        lineGeometry.setDrawRange(0, lineIdx / 3);

        particles.rotation.y += 0.0008;
        lines.rotation.y += 0.0008;

        renderer.render(scene, camera);
    }

    animate();

    function handleResize() {
        const w = container.clientWidth || window.innerWidth;
        const h = container.clientHeight || 500;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
    }

    window.addEventListener('resize', handleResize, { passive: true });
}
